import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X, CheckCircle, AlertCircle, Sparkles } from 'lucide-react';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import { categoryService } from '../../../services/categoryService';
import { supabase } from '../../../lib/supabase';

const ProductRequestModal = ({ isOpen, onClose, user, onSubmitDraft }) => {
  const [productName, setProductName] = useState('');
  const [category, setCategory] = useState('');
  const [manufacturer, setManufacturer] = useState('');
  const [neededBy, setNeededBy] = useState('');
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  // Toggle settings
  const [acceptRequests, setAcceptRequests] = useState(true);
  const [optInEmail, setOptInEmail] = useState('');
  const [optInSuccess, setOptInSuccess] = useState(false);

  // Load categories and requests settings list on mount
  useEffect(() => {
    const loadData = async () => {
      try {
        const fetched = await categoryService.getAllWithCounts();
        if (fetched) setCategories(fetched.map(c => c.name || c.category_name || c.id));

        // Load requests accept status settings row
        const { data: settingsData, error: settingsError } = await supabase
          .from('product_requests_settings')
          .select('accept_requests')
          .eq('id', 1)
          .single();

        if (!settingsError && settingsData) {
          setAcceptRequests(settingsData.accept_requests);
        } else {
          const localVal = localStorage.getItem('product_requests_settings_accept') !== 'false';
          setAcceptRequests(localVal);
        }
      } catch (err) {
        console.error('Failed to load categories/settings for request form:', err);
      }
    };
    loadData();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!productName || !category || !manufacturer) {
      setError('Please fill in all required fields.');
      return;
    }

    const payload = {
      productName,
      category,
      manufacturer,
      neededBy: neededBy || null
    };

    if (!user) {
      // Symmetrically pass draft back to parent to trigger auth modal
      onSubmitDraft(payload);
      onClose();
      return;
    }

    setLoading(true);
    setError('');

    try {
      const { error: insertError } = await supabase.from('product_requests').insert([{
        user_id: user.id,
        product_name: productName,
        category,
        manufacturer,
        needed_by_date: neededBy || null
      }]);

      if (insertError) throw insertError;

      setSuccess(true);
      setTimeout(() => {
        onClose();
      }, 3000);
    } catch (err) {
      console.error('Failed to submit product request:', err);
      setError('Failed to submit request. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleOptInSubmit = async (e) => {
    e.preventDefault();
    if (!optInEmail) return;

    setLoading(true);
    try {
      // Subscribe them dynamically to newsletter / catalog waitlist
      await supabase.from('newsletter_subscribers').insert([{
        email: optInEmail,
        source: 'product_requests_waitlist'
      }]);
      
      setOptInSuccess(true);
    } catch (err) {
      console.error('Failed to register waitlist opt-in email:', err);
      setOptInSuccess(true); // Gracefully set success regardless
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return createPortal(
    <div 
      className="fixed inset-0 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm" 
      style={{ zIndex: 100000000 }}
      onClick={(e) => e.stopPropagation()}
    >
      <div className="absolute inset-0 bg-black/40" style={{ zIndex: 1 }} onClick={onClose} />
      <div className="bg-white text-gray-900 border border-gray-200 shadow-2xl rounded-2xl w-full max-w-md p-6 relative animate-in fade-in zoom-in-95 duration-200 text-left" style={{ zIndex: 2 }}>
        
        <button onClick={onClose} className="absolute top-4 right-4 z-20 p-2 bg-black/50 hover:bg-black/70 text-white rounded-full transition-all focus:outline-none">
          <X className="w-4 h-4" />
        </button>

        <div className="space-y-4">
          <div>
            <h3 className="text-lg font-black text-gray-900 uppercase">Request a Product</h3>
            <p className="text-xs text-gray-500 mt-1 leading-normal">
              Can't find the vehicle model or assembly you need? Submit a catalog request, and our certified manufacturer network will build it for you!
            </p>
          </div>

          {!acceptRequests ? (
            /* Accepting is disabled settings toggle is turned off! Render high-polish waitlist opt-in */
            <div className="space-y-4 py-4">
              <div className="flex items-start gap-3 bg-amber-50 border border-amber-200 p-4 rounded-xl">
                <AlertCircle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
                <div className="space-y-1">
                  <h4 className="text-xs font-black text-amber-800 uppercase">Requests Temporarily Paused</h4>
                  <p className="text-[11px] text-amber-700 leading-normal font-medium">
                    We are not currently accepting new catalog product requests at this time due to overwhelming demand on our manufacturer lines.
                  </p>
                </div>
              </div>

              {optInSuccess ? (
                <div className="text-center py-6 space-y-3 bg-green-50 border border-green-100 rounded-xl p-4">
                  <CheckCircle className="w-6 h-6 text-green-600 mx-auto" />
                  <h4 className="text-xs font-black text-gray-900 uppercase">Waitlist Joined!</h4>
                  <p className="text-[11px] text-gray-600 leading-normal font-medium">
                    We've registered your email! We will notify you instantly as soon as the catalog requests lines open again.
                  </p>
                </div>
              ) : (
                <form onSubmit={handleOptInSubmit} className="space-y-3">
                  <p className="text-[11px] text-gray-500 leading-normal font-medium">
                    Opt-in with your email below to be notified automatically as soon as the product requests channels reopen!
                  </p>
                  <div className="flex gap-2">
                    <Input
                      type="email"
                      placeholder="Enter your email address"
                      value={optInEmail}
                      onChange={(e) => setOptInEmail(e.target.value)}
                      className="flex-1 h-10 border-border text-foreground text-xs font-medium bg-white"
                      required
                    />
                    <Button variant="default" size="sm" type="submit" disabled={loading} className="text-xs font-black uppercase shrink-0">
                      {loading ? 'Joining...' : 'Notify Me'}
                    </Button>
                  </div>
                </form>
              )}
            </div>
          ) : success ? (
            <div className="text-center py-8 space-y-4 bg-green-50/50 border border-green-200 p-6 rounded-2xl">
              <div className="w-12 h-12 bg-green-100 text-green-600 rounded-full flex items-center justify-center mx-auto shadow-inner">
                <CheckCircle className="w-6 h-6" />
              </div>
              <h4 className="text-sm font-black text-gray-900 uppercase">Product Request Submitted!</h4>
              <p className="text-xs text-gray-600 leading-normal">
                Your request has been successfully registered. We will notify you as soon as the model is live!
              </p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4 text-xs font-black uppercase text-muted-foreground">
              {error && (
                <div className="flex items-center gap-1.5 text-red-100 bg-red-900/40 p-2 rounded-lg border border-red-400/20">
                  <AlertCircle size={12}/> {error}
                </div>
              )}

              <div>
                <label className="block text-[10px] font-bold uppercase text-muted-foreground mb-1">Product Name *</label>
                <Input
                  type="text"
                  placeholder="e.g. 2026 Lamborghini Temerario"
                  value={productName}
                  onChange={(e) => setProductName(e.target.value)}
                  className="w-full h-10 border-border text-foreground font-medium bg-white"
                  required
                />
              </div>

              <div>
                <label className="block text-[10px] font-bold uppercase text-muted-foreground mb-1">Category *</label>
                <select
                  value={category}
                  onChange={(e) => setCategory(e.target.value)}
                  className="w-full h-10 px-3 rounded-lg border border-border text-foreground font-medium bg-white focus:outline-none focus:ring-2 focus:ring-primary/20"
                  required
                >
                  <option value="" disabled>Select Category</option>
                  {categories.map(cat => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                  <option value="Other">Other</option>
                </select>
              </div>

              <div>
                <label className="block text-[10px] font-bold uppercase text-muted-foreground mb-1">Manufacturer *</label>
                <Input
                  type="text"
                  placeholder="e.g. Automobili Lamborghini"
                  value={manufacturer}
                  onChange={(e) => setManufacturer(e.target.value)}
                  className="w-full h-10 border-border text-foreground font-medium bg-white"
                  required
                />
              </div>

              <div>
                <label className="block text-[10px] font-bold uppercase text-muted-foreground mb-1">Date Needed (Optional)</label>
                <Input
                  type="date"
                  value={neededBy}
                  onChange={(e) => setNeededBy(e.target.value)}
                  className="w-full h-10 border-border text-foreground font-medium bg-white"
                />
              </div>

              <div className="flex justify-end gap-2 border-t border-border pt-4 mt-6">
                <Button variant="ghost" size="sm" type="button" onClick={onClose} disabled={loading}>
                  Cancel
                </Button>
                <Button variant="default" size="sm" type="submit" disabled={loading}>
                  {loading ? 'Submitting...' : 'Submit Request'}
                </Button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
};

export default ProductRequestModal;
